/*
	Reparto parejo de la venta entre asesoras (Esteban, 2026-10-31: «si son varias, split percentages solo, no pedir que calculen»).
	La lista casi siempre es una sola persona al 100 %; con varias, partes iguales a 2 decimales y la última se queda con el resto
	para que sumen 100 exacto (3 asesoras → 33.33, 33.33, 33.34). El servidor hace el mismo reparto cuando todos los % vienen vacíos.
*/
export interface ConReparto { sharePercentage: number | null }

const round2 = (n: number) => Math.round(n * 100) / 100;

/** La misma lista con el % parejo en cada una (2 decimales; la última toma el resto). Vacía → vacía. */
export const repartoParejo = <T extends ConReparto>(list: T[]): T[] => {
	const n = list.length;
	if (n < 1) return list;
	const parte = round2(100 / n);
	const ultima = round2(100 - parte * (n - 1));
	return list.map((a, i) => ({ ...a, sharePercentage: i === n - 1 ? ultima : parte }));
};

/**
 * Lo que queda después de un cambio en la lista: si nadie ha tocado un % a mano (`touched` = false) se vuelve a repartir parejo;
 * si ya hay números escritos, se respetan y solo la asesora nueva entra en null (la pantalla pide completar la suma).
 */
export const conReparto = <T extends ConReparto>(list: T[], touched: boolean): T[] => (touched ? list : repartoParejo(list));

/** true cuando todos los % son los del reparto parejo (o están vacíos): el enlace «Reparto Parejo» no hace falta. */
export const esParejo = (list: ConReparto[]): boolean => {
	if (list.length < 1) return true;
	if (list.every((a) => a.sharePercentage === null || a.sharePercentage === undefined)) return true;
	const parejo = repartoParejo(list);
	return list.every((a, i) => Math.abs(Number(a.sharePercentage) - Number(parejo[i].sharePercentage)) < 0.005);
};
